import { useState } from 'react';
import {
  db,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  doc,
  updateDoc,
  functions,
  httpsCallable,
  writeBatch,
} from './firebaseConfig';
import { deleteField } from 'firebase/firestore';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Chip,
  Box,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const Admin = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [mismatchedDocs, setMismatchedDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingDocs, setUpdatingDocs] = useState(new Set());
  const [sendingNotification, setSendingNotification] = useState(false);
  const [testingTomorrowNotifications, setTestingTomorrowNotifications] = useState(false);
  const [issuingAlexCode, setIssuingAlexCode] = useState(false);
  const [alexCodeMessage, setAlexCodeMessage] = useState('');
  const [migratingCodes, setMigratingCodes] = useState(false);
  const [migrationResults, setMigrationResults] = useState([]);
  const [clearingLegacy, setClearingLegacy] = useState(false);
  const [clearLegacyMessage, setClearLegacyMessage] = useState('');
  const [isDownloadingInvoices, setIsDownloadingInvoices] = useState(false);

  // Date range state
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Preset date range functions
  const setPresetRange = (type) => {
    const today = new Date();
    switch (type) {
      case '2-months-past':
        setStartDate(dayjs(new Date(today.getTime() - (2 * 30 * 24 * 60 * 60 * 1000))));
        setEndDate(dayjs(today));
        break;
      case '5-months-future':
        setStartDate(dayjs(today));
        setEndDate(dayjs(new Date(today.getTime() + (5 * 30 * 24 * 60 * 60 * 1000))));
        break;
      case '25-months-future':
        setStartDate(dayjs(today));
        setEndDate(dayjs(new Date(today.getTime() + (25 * 30 * 24 * 60 * 60 * 1000))));
        break;
      case 'all-time':
        setStartDate(null);
        setEndDate(null);
        break;
      default:
        // Default to 25 months future
        setStartDate(dayjs(today));
        setEndDate(dayjs(new Date(today.getTime() + (25 * 30 * 24 * 60 * 60 * 1000))));
    }
  };

  // Get effective date range for query
  const getDateRange = () => {
    if (useCustomRange && startDate && endDate) {
      return {
        start: startDate.toDate().toISOString(),
        end: endDate.toDate().toISOString()
      };
    } else {
      // Default behavior: today to 25 months
      const today = new Date();
      const fiveMonthsLater = new Date(today.getTime() + (25 * 30 * 24 * 60 * 60 * 1000));
      return {
        start: today.toISOString(),
        end: fiveMonthsLater.toISOString()
      };
    }
  };

  const checkFieldMismatches = async () => {
    try {
      setIsLoading(true);
      console.log('Starting field mismatch check...');

      // Get effective date range
      const dateRange = getDateRange();

      console.log('Date range:', {
        from: dateRange.start,
        to: dateRange.end
      });

      // Query JobData collection for documents in date range
      const jobsQuery = query(
        collection(db, 'JobData'),
        where('bookedStart', '>=', dateRange.start),
        where('bookedStart', '<=', dateRange.end),
        orderBy('bookedStart', 'asc')
      );

      const querySnapshot = await getDocs(jobsQuery);
      console.log(`Found ${querySnapshot.size} documents in date range`);

      const mismatches = [];

      querySnapshot.forEach((document) => {
        const data = document.data();
        const docId = document.id;

        // Helper function to normalize dates for comparison
        const normalizeDate = (date) => {
          if (!date) return null;
          if (typeof date === 'string') return date;
          if (date.toDate) return date.toDate().toISOString(); // Firebase Timestamp
          if (date.seconds) return new Date(date.seconds * 1000).toISOString(); // Timestamp object
          if (date instanceof Date) return date.toISOString(); // Date object
          return String(date); // Fallback
        };

        // Compare normalized values instead of raw objects
        const normalizedBookedStart = normalizeDate(data.bookedStart);
        const normalizedStart = normalizeDate(data.start);
        const normalizedBookedFinish = normalizeDate(data.bookedFinish);
        const normalizedEnd = normalizeDate(data.end);

        const startMismatch = normalizedBookedStart !== normalizedStart;
        const endMismatch = normalizedBookedFinish !== normalizedEnd;

        if (startMismatch || endMismatch) {
          // Helper function to convert Firebase timestamps to readable strings
          const formatDate = (date) => {
            if (!date) return 'null';
            if (typeof date === 'string') return date;
            if (date.toDate) return date.toDate().toISOString();
            if (date.seconds) return new Date(date.seconds * 1000).toISOString();
            return String(date);
          };

          const mismatchInfo = {
            id: docId,
            title: data.title || data.jobTypeFull || 'Untitled Job',
            client: data.client || 'No Client',
            jobNumber: data.jobNumber || 'No Job Number',
            rawData: {
              bookedStart: data.bookedStart,
              bookedFinish: data.bookedFinish,
              start: data.start,
              end: data.end
            },
            mismatches: {
              start: startMismatch ? {
                bookedStart: formatDate(data.bookedStart),
                start: formatDate(data.start)
              } : null,
              end: endMismatch ? {
                bookedFinish: formatDate(data.bookedFinish),
                end: formatDate(data.end)
              } : null
            }
          };

          mismatches.push(mismatchInfo);
          console.log('Mismatch found:', mismatchInfo);
        }
      });

      console.log(`Total mismatches found: ${mismatches.length}`);
      setMismatchedDocs(mismatches);
      setModalOpen(true);

    } catch (error) {
      console.error('Error checking field mismatches:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const updateDocumentFields = async (docData) => {
    try {
      setUpdatingDocs(prev => new Set([...prev, docData.id]));

      console.log('Updating document:', docData.id);

      // Helper function to convert any date format to ISO string
      const toISOString = (date) => {
        if (!date) return null;
        if (typeof date === 'string') return date;
        if (date.toDate) return date.toDate().toISOString(); // Firebase Timestamp
        if (date.seconds) return new Date(date.seconds * 1000).toISOString(); // Timestamp object
        if (date instanceof Date) return date.toISOString(); // Date object
        return String(date); // Fallback
      };

      // Prepare update object - always update start/end, and also update bookedStart/bookedFinish if they're Timestamps
      const updateData = {
        start: toISOString(docData.rawData.bookedStart),
        end: toISOString(docData.rawData.bookedFinish)
      };

      // Also update bookedStart if it's a Timestamp object (not already a string)
      if (docData.rawData.bookedStart && typeof docData.rawData.bookedStart !== 'string') {
        updateData.bookedStart = toISOString(docData.rawData.bookedStart);
      }

      // Also update bookedFinish if it's a Timestamp object (not already a string)
      if (docData.rawData.bookedFinish && typeof docData.rawData.bookedFinish !== 'string') {
        updateData.bookedFinish = toISOString(docData.rawData.bookedFinish);
      }

      console.log('Update data:', updateData);

      // Update the document
      await updateDoc(doc(db, 'JobData', docData.id), updateData);

      console.log('Successfully updated document:', docData.id);

      // Remove this document from the mismatched list
      setMismatchedDocs(prev => prev.filter(doc => doc.id !== docData.id));


    } catch (error) {
      console.error('Error updating document:', error);
      alert(`Error updating ${docData.title}: ${error.message}`);
    } finally {
      setUpdatingDocs(prev => {
        const newSet = new Set(prev);
        newSet.delete(docData.id);
        return newSet;
      });
    }
  };

  // Validation for custom date range
  const isValidDateRange = () => {
    if (!useCustomRange) return true;
    if (!startDate || !endDate) return false;
    return startDate.isBefore(endDate) || startDate.isSame(endDate, 'day');
  };

  // Get button text based on current date range
  const getButtonText = () => {
    if (isLoading) return 'Checking...';

    if (useCustomRange) {
      if (!startDate || !endDate) return 'Select Start & End Dates';
      if (!isValidDateRange()) return 'Invalid Date Range';

      const formatDate = (date) => date.format('MMM D');
      return `Check Mismatches (${formatDate(startDate)} - ${formatDate(endDate)})`;
    }

    return 'Check Field Mismatches (Default: 25 months)';
  };

  // Send test notification
  const sendTestNotification = async () => {
    try {
      setSendingNotification(true);

      const sendNotificationToUID = httpsCallable(functions, 'sendNotificationToUID');

      const result = await sendNotificationToUID({
        uid: 'Q2shLKo0IYgMwRUITJcTZZQf5lB2',
        title: 'Test Notification',
        text: 'This is a test notification from the admin panel',
        kind: 'notificationTomorrowsJob'
      });

      console.log('Test notification sent:', result);
      alert('Test notification sent successfully!');
    } catch (error) {
      console.error('Failed to send test notification:', error);
      alert(`Failed to send notification: ${error.message}`);
    } finally {
      setSendingNotification(false);
    }
  };

  // Test tomorrow notifications function
  const testTomorrowNotifications = async () => {
    try {
      setTestingTomorrowNotifications(true);

      const testTomorrowNotificationsFunc = httpsCallable(functions, 'testTomorrowNotifications');

      const result = await testTomorrowNotificationsFunc();

      console.log('Tomorrow notifications test result:', result);

      if (result.data.success === false) {
        alert(`Test failed: ${result.data.error}`);
      } else {
        alert(`Test completed!\nTotal jobs found: ${result.data.totalJobs}\nNotifications sent: ${result.data.successCount}\nFailed: ${result.data.failureCount}`);
      }
    } catch (error) {
      console.error('Failed to test tomorrow notifications:', error);
      alert(`Test failed: ${error.message}`);
    } finally {
      setTestingTomorrowNotifications(false);
    }
  };

  // Test error logging by triggering a deliberate error
  const testErrorLogging = async () => {
    try {
      // Try to call a non-existent function to trigger an error
      const nonExistentFunction = httpsCallable(functions, 'nonExistentTestFunction');
      await nonExistentFunction({ test: 'data' });
    } catch (error) {
      // This error should now be logged to ErrorLogs collection
      console.log('Expected error for testing error logging:', error.message);
      alert('Test error generated! Check the ErrorLogs collection in Firestore to see if it was logged.');
    }
  };

  // Bulk share Google Sheets with team emails
  const [isSharingSheets, setIsSharingSheets] = useState(false);

  const bulkShareSheets = async () => {
    const emailsToShare = [
      'production@imagesthatsell.com.au',
      'bookings@imagesthatsell.com.au',
      'adam@imagesthatsell.com.au',
      'jodie@imagesthatsell.com.au',
      'accounts@imagesthatsell.com.au'
    ];

    setIsSharingSheets(true);
    console.time('Bulk Share Sheets');

    try {
      // Get access token using the OAuth credentials
      const tokenResponse = await fetch(
        'https://oauth2.googleapis.com/token?client_secret=rbJuk2TquUEv0A4CPugzHxyO&grant_type=refresh_token&refresh_token=1%2F%2F04yEcheFhTHtkCgYIARAAGAQSNwF-L9Ir7QptanSotXs9PIQ79w7z6VqAB7emKI1C5zxZ96F9RKs6r30LaVpFvYU8ijvCoufgPgA&client_id=123800286055-b5cfrvn8is3d8tu3c1aeosn3ppg8m354.apps.googleusercontent.com',
        {
          method: 'POST',
          headers: { 'content-type': 'application/x-www-form-urlencoded' }
        }
      ).then(res => res.json());

      const accessToken = tokenResponse.access_token;

      // Fetch all Google Sheets files
      console.log('Fetching all Google Sheets...');
      const filesResponse = await fetch(
        'https://www.googleapis.com/drive/v3/files?q=mimeType%3D%27application%2Fvnd.google-apps.spreadsheet%27&pageSize=1000',
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      ).then(res => res.json());

      const files = filesResponse.files || [];
      console.log(`Found ${files.length} spreadsheets`);

      let successCount = 0;
      let errorCount = 0;
      let skippedCount = 0;
      const errors = [];

      // Helper function to add delay for rate limiting
      const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

      // Process files in batches of 20 (increased for speed)
      for (let i = 0; i < files.length; i += 20) {
        const batch = files.slice(i, i + 20);

        for (const file of batch) {
          console.log(`Processing: ${file.name} (${file.id})`);

          try {
            const permissionsResponse = await fetch(
              `https://www.googleapis.com/drive/v3/files/${file.id}/permissions?fields=permissions(emailAddress)`,
              {
                headers: {
                  'Authorization': `Bearer ${accessToken}`
                }
              }
            );

            if (!permissionsResponse.ok) {
              if (permissionsResponse.status === 403 || permissionsResponse.status === 404) {
                skippedCount += emailsToShare.length;
                continue;
              }
              throw new Error(`HTTP ${permissionsResponse.status}: ${permissionsResponse.statusText}`);
            }

            const permissionsData = await permissionsResponse.json();
            const existingEmails = (permissionsData.permissions || [])
              .map(p => p.emailAddress)
              .filter(Boolean);

            for (const email of emailsToShare) {
              if (existingEmails.includes(email)) {
                skippedCount++;
                continue;
              }

              let attempts = 0;
              let shared = false;

              while (!shared && attempts < 2) {
                attempts += 1;

                const shareResponse = await fetch(
                  `https://www.googleapis.com/drive/v3/files/${file.id}/permissions?sendNotificationEmails=false`,
                  {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${accessToken}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      type: 'user',
                      role: 'writer',
                      emailAddress: email
                    })
                  }
                );

                if (shareResponse.ok) {
                  successCount++;
                  console.log(`Shared ${file.name} with ${email}`);
                  shared = true;
                  break;
                }

                const errorText = await shareResponse.text();

                if (shareResponse.status === 403) {
                  skippedCount++;
                  shared = true;
                  break;
                }

                if (shareResponse.status === 400) {
                  if (errorText.includes('items were successfully shared')) {
                    console.log(`Shared ${file.name} with ${email} (email notification failed but share succeeded)`);
                    successCount++;
                    shared = true;
                    break;
                  }
                  console.warn(`Invalid request for ${file.name} with ${email}: ${errorText}`);
                  errorCount++;
                  shared = true;
                  break;
                }

                if (shareResponse.status === 429 && attempts < 2) {
                  console.warn(`Rate limited - waiting 2s before retry`);
                  await sleep(2000);
                  continue;
                }

                errorCount++;
                errors.push(`${file.name} - ${email}: ${errorText}`);
                shared = true;
              }
            }
          } catch (error) {
            console.error(`Failed to process ${file.name}:`, error.message);
            errorCount++;
            errors.push(`${file.name}: ${error.message}`);
          }
        }

        console.log(`Processed batch ${Math.floor(i / 20) + 1}/${Math.ceil(files.length / 20)}`);

        // Add small delay between batches to respect rate limits
        if (i + 20 < files.length) {
          await sleep(50);
        }
      }

      console.timeEnd('Bulk Share Sheets');
      alert(`Bulk sharing complete!\n\nSheets found: ${files.length}\nSuccessful shares: ${successCount}\nSkipped (already shared): ${skippedCount}\nFailed: ${errorCount}\n\n${errorCount > 0 ? 'Check console for error details.' : ''}`);

      if (errors.length > 0) {
        console.log('Errors:', errors);
      }

    } catch (error) {
      console.timeEnd('Bulk Share Sheets');
      console.error('Bulk share failed:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSharingSheets(false);
    }
  };

  const downloadAllInvoices = async () => {
    try {
      setIsDownloadingInvoices(true);
      console.log('Fetching all xero-invoices documents...');

      const invoicesQuery = collection(db, 'xero-invoices');
      const querySnapshot = await getDocs(invoicesQuery);

      const allInvoices = [];
      querySnapshot.forEach((doc) => {
        allInvoices.push({
          id: doc.id,
          ...doc.data()
        });
      });

      console.log(`Found ${allInvoices.length} invoices.`);

      // Create and download JSON file
      const jsonString = JSON.stringify(allInvoices, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `xero-invoices-${dayjs().format('YYYY-MM-DD-HHmm')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert(`Successfully downloaded ${allInvoices.length} invoices as JSON.`);
    } catch (error) {
      console.error('Error downloading invoices:', error);
      alert(`Error downloading invoices: ${error.message}`);
    } finally {
      setIsDownloadingInvoices(false);
    }
  };

  const handleIssueAlexLoginCode = async () => {
    try {
      setIssuingAlexCode(true);
      setAlexCodeMessage('');
      const adminIssueLoginCode = httpsCallable(functions, 'adminIssueLoginCode');
      const result = await adminIssueLoginCode({
        staffDocId: '8jXVYoCymxHLQngklbWE'
      });

      const { code, issuedAt } = result?.data || {};
      if (code) {
        const issuedLabel = issuedAt
          ? new Date(issuedAt).toLocaleString()
          : 'just now';
        setAlexCodeMessage(`New code: ${code} (issued ${issuedLabel})`);
      } else {
        setAlexCodeMessage('Code issued successfully.');
      }
    } catch (error) {
      console.error('Failed to issue Alex HOTP code:', error);
      setAlexCodeMessage(error.message || 'Failed to issue code.');
    } finally {
      setIssuingAlexCode(false);
    }
  };

  const staffDocsToMigrate = [
    'GNhKHi0YGRfHQkN864T6',
    'ACLEOLP1k5AJlvwadz72',
    '3XYflM9ex68N0mZW22y1',
    'E8AkUpiW9HyhEPMylA0N',
    'SL18s54ddvvfGxW5o14r',
    'CLJqPv7r8yHYwUv87TpH'
  ];

  const handleMigrateStaffHotp = async () => {
    setMigratingCodes(true);
    setMigrationResults([]);

    const adminIssueLoginCode = httpsCallable(functions, 'adminIssueLoginCode');
    const results = [];

    for (const docId of staffDocsToMigrate) {
      try {
        const response = await adminIssueLoginCode({ staffDocId: docId });
        results.push({
          docId,
          status: 'success',
          code: response?.data?.code || 'issued'
        });
      } catch (error) {
        console.error('Failed to migrate HOTP code for', docId, error);
        results.push({
          docId,
          status: 'error',
          message: error.message || 'Unknown error'
        });
      }
    }

    setMigrationResults(results);
    setMigratingCodes(false);
  };

  const handleClearLegacyTokens = async () => {
    try {
      setClearingLegacy(true);
      setClearLegacyMessage('');
      const snapshot = await getDocs(collection(db, 'StaffData'));
      let totalCleared = 0;
      let batch = writeBatch(db);
      let opsInBatch = 0;

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        if (data?.loginToken || data?.loginTokenCreated) {
          batch.update(docSnap.ref, {
            loginToken: deleteField(),
            loginTokenCreated: deleteField(),
          });
          totalCleared += 1;
          opsInBatch += 1;

          if (opsInBatch >= 400) {
            await batch.commit();
            batch = writeBatch(db);
            opsInBatch = 0;
          }
        }
      }

      if (opsInBatch > 0) {
        await batch.commit();
      }

      setClearLegacyMessage(`Cleared legacy tokens for ${totalCleared} staff.`);
    } catch (error) {
      console.error('Failed to clear legacy login tokens:', error);
      setClearLegacyMessage(error.message || 'Failed to clear legacy tokens.');
    } finally {
      setClearingLegacy(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ padding: 2 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
            marginBottom: 3
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleIssueAlexLoginCode}
            disabled={issuingAlexCode}
          >
            {issuingAlexCode ? 'Generating Code…' : 'Refresh Alex HOTP Code'}
          </Button>
          {alexCodeMessage ? (
            <Typography variant="body2">{alexCodeMessage}</Typography>
          ) : null}
          <Button
            variant="outlined"
            onClick={handleMigrateStaffHotp}
            disabled={migratingCodes}
          >
            {migratingCodes ? 'Migrating…' : 'Migrate Selected Staff to HOTP'}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClearLegacyTokens}
            disabled={clearingLegacy}
          >
            {clearingLegacy ? 'Clearing…' : 'Remove Legacy Login Tokens'}
          </Button>
        </Box>
        {clearLegacyMessage ? (
          <Typography variant="body2" sx={{ marginBottom: 2 }}>
            {clearLegacyMessage}
          </Typography>
        ) : null}
        {migrationResults.length > 0 && (
          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Migration results
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {migrationResults.map(({ docId, status, code, message }) => (
                <li
                  key={docId}
                  style={{
                    color: status === 'success' ? '#2e7d32' : '#c62828',
                    fontWeight: status === 'success' ? 600 : 500,
                  }}
                >
                  {docId}: {status === 'success' ? `issued ${code}` : message}
                </li>
              ))}
            </ul>
          </Box>
        )}
        {/* Date Range Controls */}
        <Box sx={{ marginBottom: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={useCustomRange}
                onChange={(e) => setUseCustomRange(e.target.checked)}
              />
            }
            label="Use Custom Date Range"
          />
        </Box>

        {useCustomRange ? (
          // Custom Date Range Mode
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="h6" gutterBottom>
              Custom Date Range
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, marginBottom: 2, alignItems: 'center' }}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { minWidth: 150 }
                  }
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { minWidth: 150 }
                  }
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                }}
              >
                Clear
              </Button>
            </Box>
          </Box>
        ) : (
          // Preset Mode
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="h6" gutterBottom>
              Quick Presets
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', marginBottom: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPresetRange('2-months-past')}
              >
                2 Months Past
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPresetRange('5-months-future')}
              >
                Next 5 Months
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPresetRange('25-months-future')}
              >
                Next 25 Months
              </Button>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <button
            style={{
              margin: '10px',
              padding: '8px 16px',
              backgroundColor: '#d9534f',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1
            }}
            onClick={checkFieldMismatches}
            disabled={isLoading || (useCustomRange && !isValidDateRange())}
          >
            {getButtonText()}
          </button>

          <Button
            variant="contained"
            color="primary"
            onClick={sendTestNotification}
            disabled={sendingNotification}
            sx={{ margin: '10px' }}
          >
            {sendingNotification ? 'Sending...' : 'Send Test Notification'}
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={testTomorrowNotifications}
            disabled={testingTomorrowNotifications}
            sx={{ margin: '10px' }}
          >
            {testingTomorrowNotifications ? 'Testing...' : 'Test Tomorrow Notifications'}
          </Button>

          <Button
            variant="contained"
            color="warning"
            onClick={testErrorLogging}
            sx={{ margin: '10px' }}
          >
            Test Error Logging
          </Button>

          <Button
            variant="contained"
            color="success"
            onClick={bulkShareSheets}
            disabled={isSharingSheets}
            sx={{ margin: '10px' }}
          >
            {isSharingSheets ? 'Sharing Sheets...' : 'Bulk Share All Sheets'}
          </Button>

          <Button
            variant="contained"
            color="info"
            onClick={downloadAllInvoices}
            disabled={isDownloadingInvoices}
            sx={{ margin: '10px' }}
          >
            {isDownloadingInvoices ? 'Downloading Invoices...' : 'Download All Invoices (JSON)'}
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={async () => {
              console.log("Counting JobData...");
              const snapshot = await getDocs(collection(db, "JobData"));
              console.log(`JobData collection: ${snapshot.size} documents`);
            }}
            sx={{ margin: '10px' }}
          >
            Count JobData
          </Button>
        </Box>
      </Box>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Field Mismatch Results
          <Typography variant="subtitle2" color="textSecondary">
            Found {mismatchedDocs.length} documents with mismatched bookedStart/start or bookedFinish/end fields
          </Typography>
        </DialogTitle>

        <DialogContent>
          {mismatchedDocs.length === 0 ? (
            <Typography variant="h6" color="success.main" textAlign="center" py={3}>
              🎉 No mismatches found! All fields are synchronized.
            </Typography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Job Details</TableCell>
                  <TableCell>Mismatch Type</TableCell>
                  <TableCell>bookedStart vs start</TableCell>
                  <TableCell>bookedFinish vs end</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mismatchedDocs.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">{doc.title}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Client: {doc.client}
                        </Typography>
                        <br />
                        <Typography variant="caption" color="textSecondary">
                          Job: {doc.jobNumber} | ID: {doc.id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1} flexDirection="column">
                        {doc.mismatches.start && (
                          <Chip label="Start Mismatch" color="error" size="small" />
                        )}
                        {doc.mismatches.end && (
                          <Chip label="End Mismatch" color="warning" size="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {doc.mismatches.start ? (
                        <Box>
                          <Typography variant="caption" color="error">
                            bookedStart: {doc.mismatches.start.bookedStart}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="error">
                            start: {doc.mismatches.start.start || 'null'}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="success.main">✓ Match</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {doc.mismatches.end ? (
                        <Box>
                          <Typography variant="caption" color="warning.main">
                            bookedFinish: {doc.mismatches.end.bookedFinish}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="warning.main">
                            end: {doc.mismatches.end.end || 'null'}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="success.main">✓ Match</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => updateDocumentFields(doc)}
                        disabled={updatingDocs.has(doc.id)}
                        sx={{ minWidth: '120px' }}
                      >
                        {updatingDocs.has(doc.id) ? 'Updating...' : 'Update Fields'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Close</Button>
          {mismatchedDocs.length > 0 && (
            <Button
              onClick={() => {
                const results = JSON.stringify(mismatchedDocs, null, 2);
                navigator.clipboard.writeText(results);
                alert('Results copied to clipboard!');
              }}
              variant="outlined"
            >
              Copy Results
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default Admin;
